import { useState } from 'react';
import { MeasurementPlan } from '../../../shared/types/protocol.ts';
import { formatFallbackInterval } from '../../../shared/utils/formatFallbackInterval.ts';
import { Protocol } from '../../types/sharedTypeImpl.ts';
import { defaultMeasurementVariants } from '../../utils/editMeasurementPlan.ts';
import { IntegerInput } from '../integerInput.tsx';
import { ModalDialog } from '../modalDialog.tsx';
import { createPlanOrAlert } from './createPlanOrAlert.ts';
import { getPositionsTable } from './getPositionsTable.ts';
import { useNavigateOnArrow } from './useNavigateOnArrow.ts';


interface Props {
    protocol: Protocol;
    isViewMode: boolean;
    onSubmit: (measurementPlan: MeasurementPlan, interval: number | null) => void;
    onClose: () => void;
}

export function MeasurementPlanDialog({
    protocol,
    isViewMode,
    onSubmit,
    onClose,
}: Props) {

    const [measureInterval, setMeasureInterval] = useState<number | null>(protocol.measureInterval ?? null);
    const [fallbackInterval, setFallbackInterval] = useState<string>(
        formatFallbackInterval(protocol.measurementPlan?.fallbackIntervalMs)
    );
    const [variantSequence, setVariantSequence] = useState<string>(protocol.measurementPlan?.variantSequence ?? '');
    const [variants, setVariants] = useState(
        protocol.measurementPlan?.variants ?? defaultMeasurementVariants(protocol)
    );
    const tableRef = useNavigateOnArrow();


    function handleVariantCountChange(variantIndex: number, posId: number, count: number | null) {
        const newMeasurementPlan = [...variants];
        const variant = variants[variantIndex];
        variant.positions[posId] = count ?? 0;
        setVariants(newMeasurementPlan);
    }

    function handleSubmit() {
        const measurementPlan = createPlanOrAlert(fallbackInterval, variantSequence, variants);
        if (!measurementPlan) {
            return;
        }
        onSubmit(measurementPlan, measureInterval);
        onClose();
    }


    const positionsTable = getPositionsTable(variants, protocol.positions);

    return (
        <ModalDialog isOpen={true} onCancel={onClose}>
            <h2>Measurement plan</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>

                <table>
                    <tbody>
                        <tr>
                            <td>Measurement interval</td>
                            <td>
                                <IntegerInput
                                    style={{ width: '100%' }}
                                    value={measureInterval}
                                    onChange={setMeasureInterval}
                                    disabled={isViewMode}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>
                                <span
                                    style={{ borderBottom: '1px dotted', cursor: 'help' }}
                                    title='Interval that is used in offline mode. Format: 4h 30m'
                                >
                                    Fallback interval
                                </span>
                            </td>
                            <td>
                                <input
                                    style={{ width: '100%' }}
                                    value={fallbackInterval}
                                    onChange={(e) => setFallbackInterval(e.target.value)}
                                    disabled={isViewMode}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>Sequence</td>
                            <td>
                                <input
                                    style={{ width: '100%' }}
                                    type='text'
                                    value={variantSequence ?? ''}
                                    disabled={isViewMode}
                                    onChange={(e) => setVariantSequence(e.target.value)}
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>

                <table
                    ref={tableRef}
                    className='measurement-plan-positions'
                    style={{ marginTop: '12px' }}
                >
                    <thead>
                        <tr>
                            <th>Position</th>
                            <th>Symbol</th>
                            {variants.map((variant, index) => (
                                <th key={index}>{variant.name}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {positionsTable.map((row, positionIndex) => (
                            <tr key={positionIndex}>
                                <td>{row.positionNumber}</td>
                                <td>{row.symbol}</td>
                                {row.variants.map((variant, variantIndex) => (
                                    <td key={variantIndex}>
                                        <IntegerInput
                                            style={{ width: '60px' }}
                                            value={variant.count || null}//Show blank cell if count is 0
                                            onChange={(count) => handleVariantCountChange(variantIndex, row.positionId, count)}
                                            disabled={isViewMode}
                                        />
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className='dialog--button-bar'>
                <button onClick={onClose}>{isViewMode ? 'Close' : 'Cancel'}</button>
                {!isViewMode && <button onClick={handleSubmit}>Submit</button>}
            </div>
        </ModalDialog>
    )
}
